import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { MessageSquare, Pill, Send, ChevronLeft, Clock, CheckCircle, XCircle, AlertCircle, User, BookOpen, ChevronDown, ChevronUp, Users, FileText, Trash2, FileSearch } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import Avatar from "@/components/Avatar";
import useAvatarsByEmail from "@/hooks/useAvatarsByEmail";
import usePresenceByEmail, { isOnlineNow, lastSeenLabel } from "@/hooks/usePresenceByEmail";
import TeamTab from "@/components/TeamTab";
import ForumTermsEditor from "@/components/ForumTermsEditor";
import ForumModerationPanel from "@/components/ForumModerationPanel";
import SpruceHealthPill from "@/components/SpruceHealthPill";
import PatientFilterBar from "@/components/PatientFilterBar";
import ProviderChecklist from "@/components/ProviderChecklist";
import PatientAssignControl from "@/components/PatientAssignControl";
import ProviderComposeDialog from "@/components/ProviderComposeDialog";
import SafetyAlertsBanner from "@/components/SafetyAlertsBanner";
import { ProviderFilterProvider, useProviderFilter } from "@/hooks/useProviderFilter";

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [selectedRefill, setSelectedRefill] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { if (user?.role !== "admin") navigate("/"); }, [user, navigate]);
  if (!user || user.role !== "admin") return null;

  return (
    <ProviderFilterProvider currentUserId={user.id}>
      <div className="max-w-2xl mx-auto px-4 py-6 animate-fade-in" data-testid="provider-dashboard">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-display font-semibold">Provider Dashboard</h2>
            <p className="text-sm text-muted-foreground">Manage patient messages, medication requests, and journal entries</p>
          </div>
          <div className="flex items-center gap-2">
            <SpruceHealthPill />
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/provider/spruce-logs")}
              className="rounded-full gap-1.5 text-xs"
              data-testid="open-spruce-logs"
            >
              <FileSearch className="w-3.5 h-3.5" /> Webhook logs
            </Button>
          </div>
        </div>
        <ProviderChecklist />
        <SafetyAlertsBanner />
        <PatientFilterBar />
        <Tabs defaultValue="messages" className="w-full">
          <TabsList className="w-full mb-4">
            <TabsTrigger value="messages" className="flex-1 gap-1.5"><MessageSquare className="w-4 h-4" /> Messages</TabsTrigger>
            <TabsTrigger value="refills" className="flex-1 gap-1.5"><Pill className="w-4 h-4" /> Refills</TabsTrigger>
            <TabsTrigger value="journals" className="flex-1 gap-1.5"><BookOpen className="w-4 h-4" /> Journals</TabsTrigger>
            {user.is_super_admin && <TabsTrigger value="team" className="flex-1 gap-1.5" data-testid="team-tab-trigger"><Users className="w-4 h-4" /> Team</TabsTrigger>}
            {user.is_super_admin && <TabsTrigger value="forum-terms" className="flex-1 gap-1.5" data-testid="forum-terms-tab-trigger"><FileText className="w-4 h-4" /> Forum</TabsTrigger>}
          </TabsList>
          <TabsContent value="messages">{selectedConvo ? <ProviderConversationView convo={selectedConvo} user={user} onBack={() => setSelectedConvo(null)} /> : <ConversationList onSelect={setSelectedConvo} />}</TabsContent>
          <TabsContent value="refills">{selectedRefill ? <RefillDetail refill={selectedRefill} user={user} onBack={() => setSelectedRefill(null)} /> : <RefillList onSelect={setSelectedRefill} />}</TabsContent>
          <TabsContent value="journals"><PatientJournals /></TabsContent>
          {user.is_super_admin && <TabsContent value="team"><TeamTab currentUserId={user.id} /></TabsContent>}
          {user.is_super_admin && <TabsContent value="forum-terms">
            <div className="space-y-8">
              <ForumModerationPanel />
              <div className="border-t border-border" />
              <ForumTermsEditor />
            </div>
          </TabsContent>}
        </Tabs>
      </div>
    </ProviderFilterProvider>
  );
}

function ConversationList({ onSelect }) {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [toDelete, setToDelete] = useState(null); // {id, subject, patient_email}
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const { isEmailVisible } = useProviderFilter() || {};
  useEffect(() => { API.get("/conversations").then(({ data }) => { setConvos(data || []); setLoading(false); }).catch(() => setLoading(false)); }, [refreshKey]);
  const filtered = isEmailVisible ? convos.filter(c => isEmailVisible(c.patient_email)) : convos;
  const emails = filtered.map(c => c.patient_email);
  const avatars = useAvatarsByEmail(emails);
  const presence = usePresenceByEmail(emails);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const { data } = await API.delete(`/conversations/${toDelete.id}`);
      setConvos(prev => prev.filter(c => c.id !== toDelete.id));
      toast({
        title: "Conversation deleted",
        description: `Removed thread with ${toDelete.patient_email} (${data.messages_deleted} message${data.messages_deleted === 1 ? "" : "s"}).`,
        duration: 3500,
      });
      setToDelete(null);
    } catch (err) {
      toast({
        title: "Couldn't delete",
        description: err?.response?.data?.detail || "Try again",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{filtered.length} {filtered.length === 1 ? "conversation" : "conversations"}</p>
        <ProviderComposeDialog onSent={() => setRefreshKey(k => k + 1)} />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-7 h-7 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>
      ) : !filtered.length ? (
        <p className="text-center text-sm text-muted-foreground py-12">{convos.length ? "No patients match this filter." : "No patient conversations yet. Start one with the New message button above."}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => {
            const online = isOnlineNow(presence[c.patient_email]);
            const seen = lastSeenLabel(presence[c.patient_email]);
            return (
              <div key={c.id} className="group relative" data-testid="provider-convo-item">
                <button onClick={() => onSelect(c)} className="w-full text-left p-4 bg-card rounded-xl border border-border hover:border-primary/50 transition-all">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar src={avatars[c.patient_email]} name={c.patient_name} email={c.patient_email} size="sm" online={online} className="shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{c.subject}</p>
                        <p className="text-xs text-muted-foreground truncate">{c.patient_email}</p>
                        {c.last_message_preview && <p className="text-xs text-muted-foreground truncate mt-0.5 pr-7">{c.last_message_preview}</p>}
                        <p className={`text-[10px] mt-0.5 ${online ? "text-green-600 font-semibold" : "text-muted-foreground"}`} data-testid="provider-convo-presence">{seen}</p>
                      </div>
                    </div>
                    {c.unread_count > 0 && <span className="w-5 h-5 bg-secondary text-secondary-foreground text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">{c.unread_count}</span>}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setToDelete({ id: c.id, subject: c.subject, patient_email: c.patient_email }); }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Delete conversation"
                  data-testid={`provider-convo-delete-${c.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
        <AlertDialogContent data-testid="provider-convo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the entire thread with{" "}
              <span className="font-medium text-foreground">{toDelete?.patient_email}</span>, including every message in it. It will not remove anything from Spruce.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="provider-convo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-600"
              data-testid="provider-convo-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete thread"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProviderConversationView({ convo, user, onBack }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(() => API.get(`/messages/${convo.id}`).then(({ data }) => setMessages(data || [])).catch(() => {}), [convo.id]);
  useEffect(() => { fetchMessages(); const interval = setInterval(fetchMessages, 10000); return () => clearInterval(interval); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    await API.post("/messages", { conversation_id: convo.id, sender_email: user.email, sender_name: user.full_name || "Provider", sender_role: "provider", content: reply.trim(), is_read: false });
    await API.put(`/conversations/${convo.id}`, { last_message_preview: reply.trim().slice(0, 80), last_message_date: new Date().toISOString(), unread_count: 0 });
    await API.post("/notifications", { patient_email: convo.patient_email, title: "New message from your provider", body: reply.trim().slice(0, 100), type: "new_message", is_read: false, link_to: "/messages" });
    setReply(""); setSending(false); fetchMessages();
  };

  return (
    <div className="flex flex-col h-[70vh]">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-accent"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex-1 min-w-0"><p className="font-medium text-sm">{convo.subject}</p><p className="text-xs text-muted-foreground truncate">{convo.patient_email}</p></div>
      </div>
      <div className="mb-3"><PatientAssignControl patientEmail={convo.patient_email} /></div>
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.map(msg => {
          const isProvider = msg.sender_role === "provider" || msg.sender_role === "staff";
          return (
            <div key={msg.id} className={`flex ${isProvider ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 ${isProvider ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-accent text-foreground rounded-bl-sm"}`}>
                <p className="text-sm leading-relaxed">{msg.content}</p>
                <p className={`text-[10px] mt-1 ${isProvider ? "opacity-70" : "text-muted-foreground"}`}>{msg.sender_name} - {msg.created_date ? new Date(msg.created_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="flex gap-2 pt-3 border-t border-border mt-3">
        <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Write a reply..." rows={2} className="resize-none" onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }} data-testid="provider-reply-input" />
        <Button onClick={handleSend} disabled={!reply.trim() || sending} size="icon" className="self-end shrink-0" data-testid="provider-send-button"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function RefillList({ onSelect }) {
  const [refills, setRefills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { toast } = useToast();
  const { isEmailVisible } = useProviderFilter() || {};
  const reload = () => API.get("/refill-requests").then(({ data }) => { setRefills(data || []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { reload(); }, []);
  const filtered = isEmailVisible ? refills.filter(r => isEmailVisible(r.patient_email)) : refills;
  const emails = filtered.map(r => r.patient_email);
  const avatars = useAvatarsByEmail(emails);
  const presence = usePresenceByEmail(emails);
  const statusConfig = { pending: { color: "text-amber-600 bg-amber-50 border-amber-200", icon: Clock }, under_review: { color: "text-blue-600 bg-blue-50 border-blue-200", icon: Clock }, approved: { color: "text-green-600 bg-green-50 border-green-200", icon: CheckCircle }, denied: { color: "text-red-600 bg-red-50 border-red-200", icon: XCircle }, completed: { color: "text-gray-600 bg-gray-50 border-gray-200", icon: CheckCircle } };

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const allActionableIds = filtered.filter(r => r.status !== "completed" && r.status !== "denied").map(r => r.id);
  const toggleAll = () => setSelected(prev => prev.size === allActionableIds.length ? new Set() : new Set(allActionableIds));

  const bulkApply = async (status) => {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const { data } = await API.post("/refill-requests/bulk-action", { ids, status });
      toast({
        title: `${data.updated} refill${data.updated === 1 ? "" : "s"} ${status.replace("_"," ")}`,
        description: status === "completed" ? "Patients will see their refills marked filled." : "",
      });
      setSelected(new Set());
      await reload();
    } catch {
      toast({ title: "Couldn't update refills", variant: "destructive" });
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-7 h-7 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;
  if (!filtered.length) return <p className="text-center text-sm text-muted-foreground py-12">{refills.length ? "No refills match this filter." : "No medication requests yet."}</p>;

  return (
    <div className="space-y-2">
      {/* Bulk-action bar (only renders when something is selected, or as a select-all hint) */}
      <div className="flex items-center gap-2 px-1 py-1 sticky top-0 z-10 bg-background" data-testid="refill-bulk-bar">
        {allActionableIds.length > 1 && (
          <button
            type="button"
            onClick={toggleAll}
            className="text-xs text-primary font-medium hover:underline"
            data-testid="refill-bulk-toggle-all"
          >
            {selected.size === allActionableIds.length ? "Clear" : `Select all ${allActionableIds.length} actionable`}
          </button>
        )}
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-1.5" data-testid="refill-bulk-actions">
            <span className="text-xs text-muted-foreground">{selected.size} selected</span>
            <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" disabled={bulkBusy} onClick={() => bulkApply("completed")} data-testid="refill-bulk-complete">
              <CheckCircle className="w-3 h-3 mr-1" /> Complete
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" disabled={bulkBusy} onClick={() => bulkApply("approved")} data-testid="refill-bulk-approve">
              Approve
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-7 text-xs text-red-600 hover:text-red-700" disabled={bulkBusy} onClick={() => bulkApply("denied")} data-testid="refill-bulk-deny">
              <XCircle className="w-3 h-3 mr-1" /> Deny
            </Button>
          </div>
        )}
      </div>

      {filtered.map(r => {
        const cfg = statusConfig[r.status] || statusConfig.pending; const StatusIcon = cfg.icon;
        const online = isOnlineNow(presence[r.patient_email]);
        const selectable = r.status !== "completed" && r.status !== "denied";
        const isSel = selected.has(r.id);
        return (
          <div key={r.id} className={`flex items-stretch bg-card rounded-xl border ${isSel ? "border-primary" : "border-border"} hover:border-primary/50 transition-all overflow-hidden`} data-testid="provider-refill-item">
            {selectable && (
              <label className="flex items-center justify-center px-3 cursor-pointer border-r border-border" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggle(r.id)}
                  data-testid={`refill-select-${r.id}`}
                  className="w-4 h-4 accent-primary"
                />
              </label>
            )}
            <button onClick={() => onSelect(r)} className="flex-1 text-left p-4 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar src={avatars[r.patient_email]} name={r.patient_name} email={r.patient_email} size="sm" online={online} className="shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm flex items-center gap-1.5">
                      {r.medication} {r.strength}
                      {r.is_schedule_2 && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Schedule II controlled substance">C-II</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.patient_name} - {r.patient_email}</p>
                  </div>
                </div>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${cfg.color}`}><StatusIcon className="w-3 h-3" />{r.status?.replace("_", " ")}</span>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

function RefillDetail({ refill, user, onBack }) {
  const [status, setStatus] = useState(refill.status);
  const [notes, setNotes] = useState(refill.staff_notes || "");
  const [saving, setSaving] = useState(false);
  const [cadence, setCadence] = useState(null);

  useEffect(() => {
    if (!refill?.patient_email) return;
    API.get(`/provider/refill-cadence?patient_email=${encodeURIComponent(refill.patient_email)}`)
      .then(({ data }) => setCadence(data))
      .catch(() => setCadence(null));
  }, [refill?.patient_email]);

  const medKey = (name) => (name || "").replace(/\([^)]*\)/g, "").replace(/\s+/g, " ").trim().toLowerCase();
  const currentMed = cadence?.medications?.find(m => m.med_key === medKey(refill.medication));

  const handleSave = async () => {
    setSaving(true);
    await API.put(`/refill-requests/${refill.id}`, { status, staff_notes: notes, reviewed_by: user?.full_name || user?.email });
    if (status !== refill.status) {
      await API.post("/notifications", { patient_email: refill.patient_email, title: "Refill Request Updated", body: `Your refill for ${refill.medication} has been ${status.replace("_", " ")}.`, type: "refill_update", is_read: false, link_to: "/refill" });
    }
    setSaving(false); onBack();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-accent"><ChevronLeft className="w-4 h-4" /></button>
        <div><p className="font-medium">{refill.medication} {refill.strength}</p><p className="text-xs text-muted-foreground">{refill.patient_name}</p></div>
      </div>
      <div className="bg-accent rounded-xl p-4 space-y-2 text-sm">
        <Row label="Patient" value={refill.patient_email} /><Row label="Medication" value={`${refill.medication} ${refill.strength}`} />
        <Row label="Pharmacy" value={refill.pharmacy_name} /><Row label="Directions" value={refill.directions || "-"} />
      </div>

      <PatientAssignControl patientEmail={refill.patient_email} />

      {/* Refill cadence (provider-only clinical insight) */}
      {currentMed && (
        <div className="bg-card border border-border rounded-xl p-4" data-testid="refill-cadence-card">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-foreground uppercase tracking-widest">Refill cadence</p>
            {currentMed.is_high_frequency && (
              <span className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 font-semibold" data-testid="high-frequency-flag">
                <AlertCircle className="w-2.5 h-2.5" /> Shorter than usual
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-[11px] text-muted-foreground">Avg days between refills</p>
              <p className="font-semibold">{currentMed.avg_days_between ? `${currentMed.avg_days_between} days` : "—"}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Most recent interval</p>
              <p className={`font-semibold ${currentMed.is_high_frequency ? "text-red-600" : ""}`}>
                {currentMed.recent_interval_days ? `${currentMed.recent_interval_days} days` : "—"}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Completed refills</p>
              <p className="font-semibold">{currentMed.completed_count} of {currentMed.request_count}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Last refill</p>
              <p className="font-semibold">
                {currentMed.last_refill_date
                  ? new Date(currentMed.last_refill_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                  : "—"}
              </p>
            </div>
          </div>
        </div>
      )}

      <div><label className="text-sm font-medium block mb-1.5">Update Status</label>
        <Select value={status} onValueChange={setStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="completed">Completed</SelectItem><SelectItem value="denied">Denied</SelectItem><SelectItem value="under_review">Under Review</SelectItem></SelectContent></Select>
      </div>
      <div><label className="text-sm font-medium block mb-1.5">Staff Notes</label><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes..." rows={3} data-testid="refill-staff-notes" /></div>
      <Button onClick={handleSave} disabled={saving} className="w-full rounded-xl" data-testid="save-refill-button">{saving ? "Saving..." : "Save & Notify Patient"}</Button>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between gap-4"><span className="text-muted-foreground shrink-0">{label}</span><span className="font-medium text-right">{value}</span></div>;
}

function PatientJournals() {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const { isEmailVisible } = useProviderFilter() || {};

  useEffect(() => {
    API.get("/journals").then(({ data }) => { setJournals(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const visibleJournals = isEmailVisible ? journals.filter(j => isEmailVisible(j.patient_email)) : journals;

  // Group by patient
  const grouped = {};
  visibleJournals.forEach(j => {
    const key = j.patient_email || j.app_user_id || "unknown";
    if (!grouped[key]) grouped[key] = { name: j.patient_name || j.patient_email || "Unknown Patient", email: j.patient_email || "", entries: [] };
    grouped[key].entries.push(j);
  });
  const patients = Object.entries(grouped).sort((a, b) => {
    const latestA = a[1].entries[0]?.created_date || "";
    const latestB = b[1].entries[0]?.created_date || "";
    return latestB.localeCompare(latestA);
  });

  const avatars = useAvatarsByEmail(patients.map(([, p]) => p.email).filter(Boolean));
  const presence = usePresenceByEmail(patients.map(([, p]) => p.email).filter(Boolean));

  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-7 h-7 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;
  if (!visibleJournals.length) return <p className="text-center text-sm text-muted-foreground py-12">{journals.length ? "No patients match this filter." : "No patient journal entries yet."}</p>;

  return (
    <div className="space-y-3" data-testid="provider-journals">
      {patients.map(([key, patient]) => {
        const online = isOnlineNow(presence[patient.email]);
        const seen = lastSeenLabel(presence[patient.email]);
        return (
        <div key={key} className="bg-card border border-border rounded-xl overflow-hidden">
          <button
            onClick={() => setExpandedPatient(expandedPatient === key ? null : key)}
            className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
            data-testid={`patient-journal-group-${key}`}
          >
            <div className="flex items-center gap-3">
              <Avatar src={avatars[patient.email]} name={patient.name} email={patient.email} size="sm" online={online} className="shrink-0" />
              <div className="text-left">
                <p className="font-medium text-sm">{patient.name}</p>
                <p className="text-xs text-muted-foreground">
                  {patient.entries.length} {patient.entries.length === 1 ? "entry" : "entries"}
                  <span className={`ml-2 ${online ? "text-green-600 font-semibold" : ""}`}>· {seen}</span>
                </p>
              </div>
            </div>
            {expandedPatient === key ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
          </button>
          {expandedPatient === key && (
            <div className="border-t border-border divide-y divide-border">
              {patient.entries.map(entry => (
                <div key={entry.id} className="animate-fade-in">
                  <button
                    onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                    className="w-full text-left px-4 py-3 hover:bg-accent/30 transition-colors"
                    data-testid={`journal-entry-${entry.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{entry.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {entry.entry_date ? new Date(entry.entry_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                          {entry.entry_time ? ` at ${entry.entry_time.slice(0, 5)}` : ""}
                        </p>
                      </div>
                      {expandedEntry === entry.id ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-1" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-1" />}
                    </div>
                  </button>
                  {expandedEntry === entry.id && (
                    <div className="px-4 pb-4 animate-fade-in">
                      <div className="bg-accent rounded-lg p-3">
                        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{entry.content}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}
